// src/components/SignUpForm.tsx
import { useUser } from "@/context/useUser";
import { getTenantId } from "@/lib/storeManager";
import SignUpFormSchema from "@/schemas/SignupFormSchema";
import navigationRoutes from "@/utils/navigationRoutes";
import { FormItem, Input, Password, Submit } from "@formily/antd-v5";
import { createForm } from "@formily/core";
import { FormProvider, createSchemaField } from "@formily/react";
import { Card } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const form = createForm({});
const signupBackgroundPath = "/assets/login-bg.avif";
const signupLogoPath = "/assets/jivandhara-logo.png";

const SchemaField = createSchemaField({
  components: { FormItem, Input, Password },
});

const SignUpForm: React.FC = () => {
  const { signup, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const tenantId = getTenantId() ?? "";

  const handleSubmit = async (values: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
  }) => {
    setLoading(true);
    const response = await signup(
      values.email,
      values.password,
      values.first_name,
      values.last_name
    );
    if (response) {
      navigate(navigationRoutes.SCHEMAS);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(navigationRoutes.SCHEMAS);
    }
  }, [isAuthenticated, tenantId, navigate]);

  return (
    <div
      className="fixed inset-0 z-0 flex min-h-screen w-screen items-center justify-center overflow-hidden p-4"
      style={{
        backgroundImage: `url("${signupBackgroundPath}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <div className="relative z-10 w-full max-w-md">
        <Card className="auth-card signup-card rounded-xl border border-slate-200/80 shadow-xl shadow-slate-900/10">
          <div className="mb-6 text-center">
            <img
              src={signupLogoPath}
              alt="Jivandhara Old Age Home Logo"
              className="mx-auto mb-3 h-20 w-auto object-contain"
            />
            <h1 className="text-2xl font-bold text-slate-800">
              Jivandhara Old Age Home
            </h1>
            <p className="mt-1 text-sm text-slate-500">Create your account</p>
          </div>

          <FormProvider form={form}>
            <SchemaField schema={SignUpFormSchema} />
            <Submit
              onSubmit={handleSubmit}
              disabled={loading}
              className="w-full rounded-md !bg-blue-600 px-4 py-2 font-medium text-white hover:!bg-blue-700"
            >
              {loading ? "Signing Up..." : "Sign Up"}
            </Submit>
          </FormProvider>

          <div className="mt-5 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <span className="text-sm text-slate-500">
                Already have an account?
              </span>
              <Link
                to={navigationRoutes.LOGIN}
                className="text-sm font-medium text-blue-600"
              >
                Login
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SignUpForm;
